import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('prayan_admin_remember');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setEmail(parsed.email || '');
        setPassword(parsed.password || '');
        setRememberMe(true);
      } catch {}
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem('admin_token', res.data.token);
      localStorage.setItem('admin_email', res.data.email || '');
      if (rememberMe) {
        localStorage.setItem('prayan_admin_remember', JSON.stringify({ email, password }));
      } else {
        localStorage.removeItem('prayan_admin_remember');
      }
      navigate('/admin/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-10 max-w-md w-full space-y-6" data-testid="admin-login-page">
        <div className="text-center space-y-2">
          <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
            <Shield className="h-7 w-7" style={{ color: '#C2410C' }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#1C1917', fontFamily: '"Cormorant Garamond", serif' }}>Admin Login</h1>
          <p className="text-sm" style={{ color: '#78716C' }}>Prayan Pilgrim Services</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          {error && <div className="rounded-lg p-3 text-sm font-medium" style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }} data-testid="login-error">{error}</div>}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Email / User ID</Label>
            <Input type="text" placeholder="admin@g.com" value={email} onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl p-4 text-base" data-testid="admin-email-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Password</Label>
            <Input type="password" placeholder="Enter password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl p-4 text-base" data-testid="admin-password-input" />
          </div>
          <div className="flex items-center space-x-2" data-testid="remember-me-section">
            <Checkbox id="remember" checked={rememberMe} onCheckedChange={(c) => setRememberMe(c)} data-testid="remember-me-checkbox" />
            <Label htmlFor="remember" className="text-sm font-medium cursor-pointer" style={{ color: '#57534E' }}>Remember me</Label>
          </div>
          <Button type="submit" disabled={loading} className="w-full text-base font-semibold p-5 rounded-xl"
            style={{ backgroundColor: '#C2410C', color: 'white' }} data-testid="admin-login-btn">
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
      </div>
    </div>
  );
}
